import { captureRumError, trackRumAction } from '@src/telemetry/rum.helper'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

/**
 * Yotpo referral funnel telemetry.
 *
 * Three ordered steps — landing via a referral URL, the referral popup opening, and the referral
 * code being generated — each with an error variant. Every emit is dual-sunk: the standard
 * `trackEvent` pipeline (InternalAnalytics / Mixpanel) plus a Datadog RUM action (or `captureRumError`
 * for the error path).
 *
 * Every emit is wrapped in `safeEmit`, so a telemetry failure can never throw into the caller. This
 * matters because callers (`useSwellSDK.showPopUp`, `ReferralModal`) invoke these from inside their
 * own `try/catch` — without the guard, a telemetry throw would be miscounted as a funnel failure.
 *
 * No referral identifiers (coupon codes, `sref_id` values, full URLs) are ever sent — only booleans,
 * low-cardinality labels, and the pathname — to keep PII/identifiers out of analytics payloads.
 */

// RUM action names mirror the telemetry event names so the funnel lines up across both sinks.
const RUM_ACTION = {
  landing: 'referral_url_landing',
  popupOpened: 'referral_popup_opened',
  emailInviteGenerated: 'referral_email_invite_generated',
  couponGenerated: 'referral_coupon_generated',
  redemptionPopupOpened: 'referral_redemption_popup_opened',
} as const

export type ReferralStep = 'landing' | 'popupOpen' | 'emailInvite' | 'couponGeneration' | 'redemptionPopupOpen'

const ERROR_EVENT = {
  landing: TELEMETRY_EVENT_NAMES.REFERRAL_URL_LANDING_ERROR,
  popupOpen: TELEMETRY_EVENT_NAMES.REFERRAL_POPUP_OPEN_ERROR,
  emailInvite: TELEMETRY_EVENT_NAMES.REFERRAL_EMAIL_INVITE_ERROR,
  couponGeneration: TELEMETRY_EVENT_NAMES.REFERRAL_COUPON_GENERATION_ERROR,
  redemptionPopupOpen: TELEMETRY_EVENT_NAMES.REFERRAL_REDEMPTION_POPUP_OPEN_ERROR,
} as const

export interface ReferralLandingProps {
  source?: string
  /** Which recognized referral param was present (e.g. 'sref_id') — the key name, never its value. */
  param?: string
  /** Landing path only, never the query string (which carries the referral identifier). */
  pathname?: string
  trafficSource?: string | null
}

export interface ReferralPopupProps {
  source?: string
  popupType?: string
}

export interface ReferralCodeProps {
  source?: string
  hasCode?: boolean
}

// Telemetry must never affect the referral flow — swallow any sink failure.
const safeEmit = (fn: () => void): void => {
  try {
    fn()
  } catch {
    // Intentionally swallowed.
  }
}

const toRumCtx = (props: object): Record<string, unknown> => ({ ...props })

/** Step 1 — shopper landed on the site via a referral URL. */
export const trackReferralUrlLanding = (props: ReferralLandingProps = {}): void => {
  safeEmit(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.REFERRAL_URL_LANDING, props)
    trackRumAction(RUM_ACTION.landing, toRumCtx(props))
  })
}

/** Step 2 — the referral popup (marketing modal / Yotpo widget / Swell rewards) opened. */
export const trackReferralPopupOpened = (props: ReferralPopupProps = {}): void => {
  safeEmit(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.REFERRAL_POPUP_OPENED, props)
    trackRumAction(RUM_ACTION.popupOpened, toRumCtx(props))
  })
}

/** Step 3a — the referrer generated an invite code by sending a refer-and-earn email invite. */
export const trackReferralEmailInviteGenerated = (props: ReferralCodeProps = {}): void => {
  safeEmit(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.REFERRAL_EMAIL_INVITE_GENERATED, props)
    trackRumAction(RUM_ACTION.emailInviteGenerated, toRumCtx(props))
  })
}

/** Step 3b — a referred friend's Yotpo discount coupon was generated. */
export const trackReferralCouponGenerated = (props: ReferralCodeProps = {}): void => {
  safeEmit(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.REFERRAL_COUPON_GENERATED, props)
    trackRumAction(RUM_ACTION.couponGenerated, toRumCtx(props))
  })
}

/** Redemption step — the Yotpo referred-customer redemption popup opened for the referred friend. */
export const trackReferralRedemptionPopupOpened = (props: ReferralPopupProps = {}): void => {
  safeEmit(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.REFERRAL_REDEMPTION_POPUP_OPENED, props)
    trackRumAction(RUM_ACTION.redemptionPopupOpened, toRumCtx(props))
  })
}

/** Error at any funnel step — emits the step's error event and a RUM error. */
export const trackReferralError = (step: ReferralStep, error: unknown, props: { source?: string } = {}): void => {
  safeEmit(() => {
    const message = error instanceof Error ? error.message : String(error)
    trackEvent(ERROR_EVENT[step], { ...props, message })
    captureRumError(error instanceof Error ? error : new Error(message), { ...props, referralStep: step })
  })
}
